import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './QuestionsStep.css';

export const MAX_QUESTIONS = 10;
export const MAX_QUESTION_LENGTH = 500;

/**
 * Seven suggested questions. This is the current 2026-05-13 set of eight with
 * the "emr" entry removed at the requester's direction (see the rework spec).
 */
export const SUGGESTED_QUESTIONS = [
  { id: 'case_mix', label: 'Case mix?', full: "What's the case mix?" },
  { id: 'w2_1099', label: 'W2 or 1099?', full: 'Is this W2 or 1099?' },
  { id: 'call', label: 'Call requirements?', full: 'Are there call requirements?' },
  { id: 'start', label: 'Start date flex?', full: 'How flexible is the start date?' },
  { id: 'mileage', label: 'Mileage reimbursed?', full: 'Is mileage reimbursed?' },
  { id: 'lodging', label: 'Per diem / lodging?', full: 'Is lodging or per diem provided?' },
  { id: 'team', label: 'Team size?', full: "What's the size of the anesthesia team?" },
];

/**
 * QuestionsStep
 *
 * Controlled. The parent owns `questions` ([{ id?, text }]); this component
 * reports every change through onQuestionsChange. Owns the suggested-chip grid
 * (toggle on tap) and the custom free-text question input. Custom questions
 * are individually removable. Optional: a provider can add zero questions.
 */
export default function QuestionsStep({ questions, onQuestionsChange }) {
  const [draftQuestion, setDraftQuestion] = useState('');

  const usedSuggestionIds = useMemo(
    () => new Set(questions.filter((q) => q.id).map((q) => q.id)),
    [questions],
  );

  const customQuestions = useMemo(
    () => questions.filter((q) => !q.id),
    [questions],
  );

  const toggleSuggestion = useCallback(
    (suggestion) => {
      if (usedSuggestionIds.has(suggestion.id)) {
        onQuestionsChange(questions.filter((q) => q.id !== suggestion.id));
        return;
      }
      if (questions.length >= MAX_QUESTIONS) return;
      onQuestionsChange([
        ...questions,
        { id: suggestion.id, text: suggestion.full },
      ]);
    },
    [questions, usedSuggestionIds, onQuestionsChange],
  );

  const addCustomQuestion = useCallback(() => {
    const trimmed = draftQuestion.trim();
    if (!trimmed) return;
    if (questions.length >= MAX_QUESTIONS) return;
    onQuestionsChange([...questions, { text: trimmed }]);
    setDraftQuestion('');
  }, [draftQuestion, questions, onQuestionsChange]);

  const removeQuestion = useCallback(
    (target) => {
      onQuestionsChange(questions.filter((q) => q !== target));
    },
    [questions, onQuestionsChange],
  );

  const atLimit = questions.length >= MAX_QUESTIONS;

  return (
    <div className="ir-questions">
      <div className="ir-qlabel">Common questions · tap to add</div>
      <div className="ir-cloud">
        {SUGGESTED_QUESTIONS.map((s) => {
          const selected = usedSuggestionIds.has(s.id);
          return (
            <button
              key={s.id}
              type="button"
              data-testid="ir-question-chip"
              onClick={() => toggleSuggestion(s)}
              disabled={!selected && atLimit}
              className={`ir-chip${selected ? ' sel' : ''}`}
              aria-pressed={selected}
            >
              {selected && (
                <span className="ir-ck" aria-hidden="true">
                  ✓
                </span>
              )}
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="ir-qlabel">Ask your own</div>
      <div className="ir-inp">
        <input
          type="text"
          className="ir-field"
          value={draftQuestion}
          onChange={(e) => setDraftQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustomQuestion();
            }
          }}
          placeholder="Type a question..."
          maxLength={MAX_QUESTION_LENGTH}
          disabled={atLimit}
        />
        <button
          type="button"
          className="ir-add"
          onClick={addCustomQuestion}
          disabled={!draftQuestion.trim() || atLimit}
        >
          Add
        </button>
      </div>

      {customQuestions.length > 0 && (
        <div className="ir-cloud ir-ownwrap">
          {customQuestions.map((q, i) => (
            <span key={`own-${i}-${q.text}`} className="ir-chip sel ir-own-pill">
              {q.text}
              <button
                type="button"
                className="ir-rm"
                onClick={() => removeQuestion(q)}
                aria-label="Remove question"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

QuestionsStep.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onQuestionsChange: PropTypes.func.isRequired,
};
